import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useState } from "react";
import { useAuthenticatedImageSrc } from "../../../hooks/useAuthenticatedImageSrc";
import styles from "./UserProductCard.module.css";

const PLAN_KEYS = ["basic", "premium", "enterprise"];

const DETAIL_GROUPS = [
  ["hosting", "database"],
  ["apiIntegrations", "support"],
  ["maintenance"],
];

export default function UserProductCard({ product }) {
  const {
    projectImage,
    projectName,
    projectUrl,
    expirationDate,
    status,
    currentPlan,
    websiteType,
  } = product;

  const { t, i18n } = useTranslation("common");
  const [imageFailed, setImageFailed] = useState(false);
  const imageSrc = useAuthenticatedImageSrc(projectImage, imageFailed);

  const formatDate = (dateString) => {
    if (!dateString) return t("notAvailable");
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return t("notAvailable");
      const loc = i18n.language?.startsWith("ro") ? "ro-RO" : "en-US";
      return date.toLocaleDateString(loc, {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return t("notAvailable");
    }
  };

  const getStatusColor = (statusValue) => {
    switch (statusValue?.toLowerCase()) {
      case "online":
        return styles.statusActive;
      case "offline":
        return styles.statusExpired;
      case "expiring soon":
        return styles.statusExpiring;
      default:
        return styles.statusDefault;
    }
  };

  const getPlanColor = (plan) => {
    switch (plan?.toLowerCase()) {
      case "basic":
        return styles.planBasic;
      case "premium":
        return styles.planPremium;
      case "enterprise":
        return styles.planEnterprise;
      default:
        return styles.planDefault;
    }
  };

  const getPlanSectionClass = (plan) => {
    switch (plan?.toLowerCase()) {
      case "premium":
        return styles.planPremiumSection;
      case "enterprise":
        return styles.planEnterpriseSection;
      default:
        return styles.planBasicSection;
    }
  };

  const rawPlan = (currentPlan || "").toLowerCase();
  const planKey = PLAN_KEYS.includes(rawPlan) ? rawPlan : "default";

  return (
    <div className={styles.productCard}>
      <div className={styles.imageSection}>
        {imageSrc ? (
          <img
            src={imageSrc}
            alt={`${projectName} preview`}
            className={styles.projectImage}
            onError={() => setImageFailed(true)}
            loading="lazy"
          />
        ) : (
          <div className={styles.projectImageFallback} aria-hidden="true" />
        )}
        <div className={`${styles.statusBadge} ${getStatusColor(status)}`}>
          {status}
        </div>

        <div className={styles.projectInfo}>
          {projectUrl ? (
            <a
              href={projectUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.projectNameLink}
            >
              <svg
                className={styles.externalLinkIcon}
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                <polyline points="15 3 21 3 21 9"></polyline>
                <line x1="10" y1="14" x2="21" y2="3"></line>
              </svg>
              <h3 className={styles.projectName}>{projectName}</h3>
            </a>
          ) : (
            <h3 className={styles.projectName}>{projectName}</h3>
          )}
          {websiteType && (
            <div className={styles.websiteType}>{websiteType}</div>
          )}
        </div>
      </div>

      <div className={styles.contentSection}>
        <div className={styles.header}>
          {projectUrl ? (
            <a
              href={projectUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.projectNameLink}
            >
              <svg
                className={styles.externalLinkIcon}
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                <polyline points="15 3 21 3 21 9"></polyline>
                <line x1="10" y1="14" x2="21" y2="3"></line>
              </svg>
              <h3 className={styles.projectName}>{projectName}</h3>
            </a>
          ) : (
            <h3 className={styles.projectName}>{projectName}</h3>
          )}
          {websiteType && (
            <div className={styles.websiteType}>{websiteType}</div>
          )}
        </div>

        <div
          className={`${styles.coloredSection} ${getPlanSectionClass(
            currentPlan
          )}`}
        >
          <div className={styles.sectionContent}>
            <div className={styles.planBadgeInSection}>
              <div
                className={`${styles.planBadgeColored} ${getPlanColor(
                  currentPlan
                )}`}
              >
                {currentPlan}
              </div>
            </div>

            <p className={styles.planSectionIntro}>{t("userProductCard.sectionTitle")}</p>

            {DETAIL_GROUPS.map((group, gi) => (
              <div key={gi} className={styles.detailGroup}>
                {group.map((key) => (
                  <div key={key} className={styles.twoColumnItem}>
                    <div className={styles.leftColumn}>
                      <strong>{t(`userProductCard.labels.${key}`)}</strong>
                    </div>
                    <div className={styles.rightColumn}>
                      {t(`userProductCard.included.${planKey}.${key}`)}
                    </div>
                  </div>
                ))}
                {gi < DETAIL_GROUPS.length - 1 ? (
                  <hr className={styles.horizontalRule} />
                ) : null}
              </div>
            ))}

            {expirationDate ? (
              <>
                <hr className={styles.horizontalRule} />
                <div className={styles.twoColumnItem}>
                  <div className={styles.leftColumn}>
                    <strong>{t("userProductCard.renewal")}</strong>
                  </div>
                  <div className={styles.rightColumn}>
                    {formatDate(expirationDate)}
                  </div>
                </div>
              </>
            ) : null}

            <div className={styles.supportFooter}>
              <p className={styles.supportFooterText}>
                {t("userProductCard.supportCta")}
              </p>
              <div className={styles.supportFooterActions}>
                <Link
                  to="/support"
                  className={`${styles.managePlanButton} ${styles.supportLinkAsButton}`}
                >
                  {t("userProductCard.supportLink")}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
